<template>
  <div class="dashboard-layout">
    <!-- Modern Sidebar -->
    <AppSidebar v-model:collapsed="sidebarCollapsed" :mobile-open="mobileNavOpen" />

    <!-- DEV-2232: на узком экране сайдбар выезжает поверх контента, под ним затемнение -->
    <Transition name="fade">
      <div v-if="mobileNavOpen" class="dashboard-layout__backdrop" @click="mobileNavOpen = false" />
    </Transition>

    <!-- Main Content Area -->
    <div class="dashboard-layout__content">
      <!-- Top Header -->
      <header class="dashboard-header">
        <!-- DEV-2232: навигация на телефоне. DEV-2501: в корне раздела её открывает
             «Меню» нижней панели, бургер — только глубже (карточка, форма). -->
        <button
          v-if="!showTabBar"
          type="button"
          class="header-burger"
          :aria-label="t('common.actions.openMenu')"
          :aria-expanded="mobileNavOpen"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <MenuIcon :size="22" />
        </button>

        <!-- DEV-2477: крошки и контекст карточки делят место, которое оставили поиск и профиль -->
        <div class="header-main">
          <div class="header-breadcrumbs">
            <nav aria-label="Breadcrumb">
              <ol class="breadcrumb">
                <template v-if="breadcrumbItems.length">
                  <li
                    v-for="(item, index) in breadcrumbItems"
                    :key="index"
                    class="breadcrumb-item"
                    :class="{ active: index === breadcrumbItems.length - 1 }"
                  >
                    <router-link v-if="item.to && index < breadcrumbItems.length - 1" :to="item.to">
                      {{ item.label }}
                    </router-link>
                    <span v-else>{{ item.label }}</span>
                  </li>
                </template>
                <template v-else>
                  <li class="breadcrumb-item">
                    <router-link to="/dashboard">{{ $t('common.navigation.home') }}</router-link>
                  </li>
                  <li v-if="currentPageTitle" class="breadcrumb-item active">
                    {{ currentPageTitle }}
                  </li>
                </template>
              </ol>
            </nav>
          </div>

          <!-- DEV-2477: контекст открытой карточки (время клиента). Карточки кладут его
               сюда через <Teleport to="#header-context">: шапка закреплена, и он виден
               при любой прокрутке и на любой вкладке. Пустой места не занимает. -->
          <div id="header-context" class="header-context" />
        </div>

        <div class="header-actions">
          <!-- Global Search (DEV-303): инлайн-поле с дропдауном подсказок -->
          <GlobalSearch ref="globalSearchRef" />

          <div class="header-actions__group">
            <!-- SIP status (DEV-425: перенесён из плавающего бейджа в топбар). DEV-2493:
                 на телефоне SIP не регистрируется — вечное «отключён» только путало. -->
            <SipStatusIndicator v-if="!onPhone" />

            <!-- Notifications Bell -->
            <button
              class="header-iconbtn header-iconbtn--bell"
              type="button"
              @click="handleNotificationsClick"
            >
              <BellIcon :size="20" />
              <span v-if="notificationCount > 0" class="header-iconbtn__badge">
                {{ notificationCount > 99 ? '99+' : notificationCount }}
              </span>
            </button>

            <!-- User Menu -->
            <div ref="userMenuRef" class="header-user">
              <button class="header-user__trigger" type="button" @click="toggleUserMenu">
                <span class="header-user__avatar">
                  <img
                    v-if="userAvatarUrl && !avatarError"
                    :src="userAvatarUrl"
                    alt=""
                    class="header-user__avatar-img"
                    @error="avatarError = true"
                  />
                  <template v-else>{{ getUserInitials() }}</template>
                </span>
                <span class="header-user__meta">
                  <span class="header-user__name">{{ currentUser.name }}</span>
                  <span class="header-user__role">{{ userRoleLabel }}</span>
                </span>
              </button>
              <Transition name="fade">
                <div v-if="userMenuOpen" class="user-menu-dropdown">
                  <router-link
                    to="/dashboard/profile"
                    class="user-menu-item"
                    @click="userMenuOpen = false"
                  >
                    <UserIcon :size="16" />
                    {{ $t('profile.title') }}
                  </router-link>
                  <button class="user-menu-item user-menu-item--danger" @click="handleLogout">
                    <LogOutIcon :size="16" />
                    {{ impersonating ? $t('impersonation.endMenu') : $t('auth.logout') }}
                  </button>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="dashboard-main">
        <router-view v-slot="{ Component }">
          <!-- key по path (без query): переход между карточками одного типа
               (/clients/A → /clients/B) меняет только params, компонент
               переиспользуется и onMounted не срабатывает — карточка залипала
               на данных предыдущей сущности (в т.ч. её сделках). Query не в
               ключе: смена вкладки (?tab=) не должна перезагружать страницу.

               DEV-2491. Без <transition mode="out-in">: уход прежней страницы
               и появление новой стоили 0,3 + 0,3 с на каждый переход. Теперь
               прежняя исчезает сразу, новая проявляется (.dashboard-main > *). -->
          <component :is="Component" :key="$route.path" />
        </router-view>
      </main>

      <MobileTabBar
        v-if="showTabBar"
        :menu-open="mobileNavOpen"
        @menu="mobileNavOpen = !mobileNavOpen"
      />
    </div>

    <!-- SIP Call Widget. Во вкладке просмотра не монтируется: он регистрирует SIP
         от имени пользователя, и его звонки пошли бы в браузер администратора (DEV-2399).
         На телефоне — тоже (DEV-2493): звонят звонилкой, а виджет всплывал бы на
         входящие с гудком и кнопками, которым нечем управлять. -->
    <ActiveCallWidget v-if="!impersonating && !onPhone" />

    <!-- Входящий звонок на общий номер WhatsApp Business (DEV-2418): видят все,
         кто работает с клиентами, на любой странице. Во вкладке просмотра сокета нет. -->
    <WabaCallOverlay v-if="!impersonating && canAnswerWabaCalls" />

    <!-- Notification Panel -->
    <NotificationPanel />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, provide, onMounted, onBeforeUnmount, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppSidebar from '@/components/AppSidebar.vue'
import MobileTabBar from '@/components/MobileTabBar.vue'
import { onStaleReturn } from '@/composables/useStaleReturn'
import { hasTabBarSections, isSectionRoot } from '@/components/sidebarNav'
import { useSectionsStore } from '@/features/auth/stores/sections.store'
import { useBackClose } from '@/design-system'
import { useReferences } from '@/features/references'
import { useAuth } from '@/features/auth'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { getPrimaryRole } from '@/features/auth/types/auth.types'
import { getCdnUrl } from '@/api/config'
import { useCallEvents } from '@/features/telephony/composables/useCallEvents'
import ActiveCallWidget from '@/features/telephony/components/ActiveCallWidget.vue'
import { isPhone } from '@/features/telephony/utils/phoneLink'
import SipStatusIndicator from '@/features/telephony/components/SipStatusIndicator.vue'
import NotificationPanel from '@/features/notifications/components/NotificationPanel.vue'
import { useNotifications } from '@/features/notifications'
import GlobalSearch from '@/features/search/components/GlobalSearch.vue'
import WabaCallOverlay from '@/features/messenger/components/WabaCallOverlay.vue'
import { useMessengerWs } from '@/features/messenger/composables/useMessengerWs'
import { useWabaCall } from '@/features/messenger/composables/useWabaCall'
import { usePermissions } from '@/features/auth/composables/usePermissions'
import { useListReturnNav } from '@/composables/useListReturnNav'
import { onClickOutside, useMediaQuery } from '@vueuse/core'
// Icons
import { BellIcon, UserIcon, LogOutIcon, MenuIcon } from 'lucide-vue-next'

// Composables
const { loadAll: loadReferences } = useReferences()
const { logout, user } = useAuth()
const authStore = useAuthStore()
// Вкладка просмотра «глазами пользователя» (DEV-2399).
const impersonating = authStore.impersonation !== null
// DEV-2493. Телефон звонит своей звонилкой: SIP-индикатор и виджет звонка там не нужны.
const onPhone = isPhone()
const { t } = useI18n()

// Аватар пользователя для топбара: полный URL из user store (после загрузки аватара)
// или из настроек профиля (avatar_url → CDN); иначе кружок с инициалами (DEV-976).
const userAvatarUrl = computed(() => {
  if (user.value?.avatar) return user.value.avatar
  const path = authStore.userSettings?.avatar_url
  return path ? getCdnUrl(path) : null
})
// Битая/удалённая картинка → фолбэк на инициалы; сброс при смене URL.
const avatarError = ref(false)
watch(userAvatarUrl, () => {
  avatarError.value = false
})
const callEvents = useCallEvents()
const {
  unreadCount: notificationCount,
  togglePanel,
  startPolling,
  stopPolling,
  fetchUnreadCount,
} = useNotifications()
// DEV-2502. Вернулись во вкладку после долгого отсутствия — счётчик колокольчика сразу,
// а не через полминуты опроса.
onStaleReturn(() => void fetchUnreadCount())

provide('callEvents', callEvents)

// Единый сокет мессенджеров живёт всю сессию, а не только в карточке клиента:
// входящий на общий номер WABA должен дойти на любой странице (DEV-2418).
// Выход во время разговора: HANGUP должен уйти до закрытия сокета, а хуки
// размонтирования идут в порядке регистрации — этот раньше useMessengerWs.
const wabaCall = useWabaCall()
onBeforeUnmount(() => {
  if (wabaCall.isActive.value) wabaCall.hangup()
})
useMessengerWs()
const { canAnswerWabaCalls } = usePermissions()

// State
const route = useRoute()

// DEV-959: хлебные крошки «Клиенты»/«Сделки»/«Агенты» ведут в список с сохранёнными
// фильтрами (запомненный URL), а не на голый путь.
const clientsReturnNav = useListReturnNav('clients')
const dealsReturnNav = useListReturnNav('deals')
const agentsReturnNav = useListReturnNav('agents')
const propertiesReturnNav = useListReturnNav('properties')
const videomeetingsReturnNav = useListReturnNav('videomeetings')
const listReturnPath: Record<string, () => string> = {
  '/dashboard/clients': () => clientsReturnNav.resolveListPath('/dashboard/clients'),
  '/dashboard/deals': () => dealsReturnNav.resolveListPath('/dashboard/deals'),
  '/dashboard/agents': () => agentsReturnNav.resolveListPath('/dashboard/agents'),
  '/dashboard/properties': () => propertiesReturnNav.resolveListPath('/dashboard/properties'),
  '/dashboard/videomeetings': () =>
    videomeetingsReturnNav.resolveListPath('/dashboard/videomeetings'),
}

const sidebarCollapsed = ref(false)
// DEV-2232: выехавшая навигация на <900px. Закрывается тапом по бэкдропу,
// Escape и любым переходом — иначе после выбора пункта она перекрывает страницу.
// DEV-2500: и системным «назад» — страница остаётся.
const mobileNavOpen = ref(false)
// DEV-2501. Ниже 900px (там, где сайдбар уезжает) в корне раздела — нижняя панель.
const narrow = useMediaQuery('(max-width: 900px)')
const sectionsStore = useSectionsStore()
// DEV-2501. Без единого доступного раздела панель — одна кнопка «Меню» во всю
// ширину, тогда остаётся бургер.
const showTabBar = computed(
  () =>
    narrow.value && isSectionRoot(route.path) && hasTabBarSections(sectionsStore.navigationKeys),
)
useBackClose(mobileNavOpen, () => {
  mobileNavOpen.value = false
})
const dynamicBreadcrumbData = ref<Record<string, string>>({})
const userMenuOpen = ref(false)
const userMenuRef = ref<HTMLElement | null>(null)
const globalSearchRef = ref<InstanceType<typeof GlobalSearch> | null>(null)

onClickOutside(userMenuRef, () => {
  userMenuOpen.value = false
})

// User data из auth store
const currentUser = computed(
  () =>
    user.value || {
      name: 'User',
      role: 'Guest',
      email: '',
    },
)

// Локализованная роль: берём старшую роль, а не первую из user.roles (DEV-196).
const userRoleLabel = computed(() => {
  const u = user.value
  const roles = u && 'roles' in u ? u.roles : undefined
  const roleKey = getPrimaryRole(roles) || 'user'
  return t(`auth.roles.${roleKey}`)
})

// Provide for child components to update dynamic breadcrumbs
provide('updateBreadcrumb', (key: string, value: string) => {
  dynamicBreadcrumbData.value[key] = value
})

// DEV-2491. Имя из прошлой карточки не должно висеть в шапке новой, пока она грузится.
watch(
  () => route.path,
  () => {
    dynamicBreadcrumbData.value = {}
  },
)

// Cmd/Ctrl+K — фокус на инлайн-поле поиска в топбаре (DEV-303)
const handleSearchHotkey = (e: KeyboardEvent) => {
  if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault()
    globalSearchRef.value?.focus()
  }
  // Esc обработан — не запрос закрытия для слоя под меню (Android с клавиатурой).
  if (e.key === 'Escape' && mobileNavOpen.value) {
    e.preventDefault()
    mobileNavOpen.value = false
  }
}

watch(
  () => route.fullPath,
  () => {
    mobileNavOpen.value = false
  },
)

// Lifecycle - загрузка справочников и запуск polling уведомлений
onMounted(async () => {
  window.addEventListener('keydown', handleSearchHotkey)
  // Отказ справочника стор ловит и логирует сам (DEV-1801) — здесь ловить нечего.
  await loadReferences()
  startPolling()
})

onUnmounted(() => {
  stopPolling()
  window.removeEventListener('keydown', handleSearchHotkey)
})

// Helper function to translate breadcrumb labels
const translateBreadcrumb = (label: string): string => {
  if (!label) return ''

  // Handle dynamic breadcrumbs. DEV-2491: пока страница не прислала имя —
  // многоточие, а не шаблон «:name» / «:title».
  if (label.startsWith(':')) {
    const key = label.slice(1)
    return dynamicBreadcrumbData.value[key] || '…'
  }

  // Handle i18n keys
  let translationKey = label

  // Format 1: i18n:key.path - REMOVE prefix
  if (label.startsWith('i18n:')) {
    translationKey = label.slice(5)
    return t(translationKey)
  }
  // Format 2: key.path (looks like i18n key)
  else if (label.includes('.') && !label.includes(' ')) {
    const translated = t(label)
    return translated !== label ? translated : label
  }

  // Not an i18n key, return as is
  return label
}

// Computed
const breadcrumbItems = computed(() => {
  const meta = route.meta?.breadcrumbs as Array<{ label: string; to?: string; dynamic?: boolean }>
  if (!meta || !Array.isArray(meta)) return []

  return meta.map((item) => {
    const resolver = item.to ? listReturnPath[item.to] : undefined
    return {
      ...item,
      to: resolver ? resolver() : item.to,
      label: translateBreadcrumb(item.label),
    }
  })
})

const currentPageTitle = computed(() => {
  // Можно настроить маппинг роутов на заголовки
  const titles: Record<string, string> = {
    '/dashboard/clients': 'Клиенты',
    '/dashboard/deals': 'Сделки',
    '/dashboard/properties': 'Проекты',
    '/dashboard/settings': 'Настройки',
    '/dashboard/reports': 'Отчеты',
    '/dashboard/calls': 'Звонки',
    '/dashboard/documents': 'Документы',
  }

  return titles[route.path] || ''
})

// Methods
const getUserInitials = () => {
  const name = currentUser.value?.name || 'User'
  const names = name.split(' ').filter((n) => n.length > 0)
  if (names.length >= 2 && names[0] && names[1]) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

const handleLogout = async () => {
  // Во вкладке просмотра «выйти» — закончить просмотр, а не сеанс пользователя.
  if (impersonating) {
    await authStore.endImpersonation()
    return
  }

  try {
    // На вход уводит сам стор: второй переход спросил бы заполненную форму ещё раз.
    await logout()
  } catch (error) {
    console.error('Logout error:', error)
  }
}

const handleNotificationsClick = () => {
  togglePanel()
}

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value
}
</script>

<style scoped>
.dashboard-layout {
  /* DEV-425: высота хедера вынесена в переменную — её вычитают full-height экраны
     (например канбан сделок), чтобы не плодить «магические» 64px по месту. */
  --app-header-height: 64px;
  display: flex;
  /* Плашка просмотра «глазами пользователя» стоит в потоке над лэйаутом: без вычета
     страница (и экран чата, рассчитанный ровно на экран) прокручивалась на её высоту. */
  min-height: calc(100vh - var(--impersonation-banner-height, 0px));
  background: var(--color-background-secondary);
}

/* Content Area */
.dashboard-layout__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* Header */
.dashboard-header {
  height: var(--app-header-height);
  background: var(--color-background-elevated);
  border-bottom: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  position: sticky;
  /* DEV-2399: под плашкой вкладки просмотра, если она есть */
  top: var(--impersonation-banner-height, 0px);
  z-index: var(--z-index-header, 30);
}

/* DEV-2477: это место — контейнер header-main. По нему виджет карточки решает, что
   показать: ширина окна не годится, чип профиля бывает от 50 до 300px. min-width: 0 —
   шапку за край не выталкивает ни виджет, ни его отступы. */
.header-main {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: var(--spacing-sm);
  container: header-main / inline-size;
}

.header-breadcrumbs {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  /* DEV-2477: @container ниже меряют место под крошки, а не окно */
  container-type: inline-size;
}

/* Виджет карточки не сжимается: обрывок времени («19») читается неверно. Что ему
   показать на этом месте, он решает сам. */
.header-context {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  margin-right: var(--spacing-sm);
}

.header-context:empty {
  display: none;
}

/* DEV-2232: бургер виден только на мобильном брейкпоинте */
.header-burger {
  display: none;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  margin-left: -10px;
  border: none;
  border-radius: var(--radius-md, 10px);
  background: transparent;
  color: var(--color-text-secondary);
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.header-burger:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

/* DEV-2232: затемнение под выехавшей навигацией; сайдбар выше него.
   DEV-2235: слой из шкалы ДС — затемнение строго между шапкой и сайдбаром. */
.dashboard-layout__backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--z-index-backdrop, 35);
  background: rgba(0, 0, 0, 0.45);
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  min-width: 0;
  white-space: nowrap;
}

/* DEV-2233: уровень сжимается многоточием, а не переносит строку. DEV-2477: и не наезжает
   на соседний («ГлавКлиенFo») — каждый режет своё. */
.breadcrumb-item,
.breadcrumb-item span,
.breadcrumb-item a {
  overflow: hidden;
  text-overflow: ellipsis;
}

.breadcrumb-item:not(:last-child)::after {
  content: '/';
  color: var(--color-text-tertiary);
  margin-left: 0.5rem;
}

.breadcrumb-item a {
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-item a:hover {
  color: var(--color-primary);
}

/* DEV-2477: на узком месте остаётся текущий уровень, как на телефоне, — иначе от каждого
   уровня остаётся «…». На совсем узком от крошек остаются буквы — прячем. Стоит после
   правил крошек: специфичность та же, решает порядок. */
@container (width < 12rem) {
  .breadcrumb-item:not(:last-child) {
    display: none;
  }
}

@container (width < 4rem) {
  .breadcrumb {
    display: none;
  }
}

.breadcrumb-item.active {
  color: var(--color-text-primary);
  font-weight: 500;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-actions__group {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Icon button (bell) */
.header-iconbtn {
  position: relative;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: var(--radius-md, 10px);
  background: transparent;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.header-iconbtn:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.header-iconbtn__badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--color-danger);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  border-radius: 9px;
  pointer-events: none;
}

/* User chip */
.header-user {
  position: relative;
}

.header-user__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 6px 4px 4px;
  border: none;
  background: transparent;
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
  transition: background 0.15s ease;
}

.header-user__trigger:hover {
  background: var(--color-bg-muted);
}

.header-user__avatar {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: var(--radius-full, 9999px);
  background: var(--color-primary);
  color: var(--color-on-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem; /* 13px */
  font-weight: 600;
  overflow: hidden;
}

.header-user__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.header-user__meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-start;
  min-width: 0;
}

.header-user__name {
  font-size: 0.84375rem; /* 13.5px */
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
}

.header-user__role {
  font-size: 0.75rem; /* 12px */
  color: var(--color-text-muted);
  white-space: nowrap;
}

/* User Menu Dropdown */
.user-menu-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 180px;
  background: var(--color-background-elevated, #fff);
  border: 1px solid var(--color-border-light, #e2e8f0);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  padding: 0.25rem;
  z-index: 50;
}

.user-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: none;
  background: transparent;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--color-text-primary, #1a1a2e);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.user-menu-item:hover {
  background: var(--color-background-secondary, #f1f5f9);
}

.user-menu-item--danger {
  color: var(--color-danger, #ef4444);
}

.user-menu-item--danger:hover {
  background: var(--color-danger-tint);
}

/* Main Content */
.dashboard-main {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

/* DEV-2491. Новая страница проявляется за 150 мс, прежняя исчезает сразу.
   Только opacity: transform на корне страницы сдвигал бы её position: fixed
   элементы (нижние панели, плавающие кнопки) на время анимации. */
.dashboard-main > * {
  animation: page-enter 0.15s ease-out;
}

@keyframes page-enter {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-main > * {
    animation: none;
  }
}

/* Responsive */
@media (max-width: 860px) {
  .header-user__meta {
    display: none;
  }
}

/* DEV-2232: ниже 900px сайдбар уезжает за экран и открывается бургером —
   при его ширине 280px во flex-ряду контенту на 390px оставалось 110px. */
@media (max-width: 900px) {
  .header-burger {
    display: flex;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 0 1rem;
    gap: 0.5rem;
  }

  /* Отступы здесь даёт gap шапки. */
  .header-context {
    margin: 0;
  }

  .dashboard-main {
    padding: 1rem;
  }
}

/* DEV-2242: колокол и профиль — полноценные тап-цели на тач-экране */
@media (pointer: coarse) {
  .header-iconbtn {
    width: 44px;
    height: 44px;
  }

  .header-user__trigger {
    min-height: 44px;
  }
}

/* DEV-2502. Ниже 641px тосты — сверху, под шапкой CRM: внизу они закрывали «Отправить»,
   поле ввода и нижние панели. И въезжают сверху. Контейнер тостов живёт в body, куда
   --app-header-height не наследуется, — берётся её значение по умолчанию. */
@media (max-width: 640px) {
  :global(html:has(.dashboard-layout) .toast-container) {
    top: calc(
      var(--app-header-height, 64px) + var(--impersonation-banner-height, 0px) +
        var(--spacing-sm, 8px)
    );
    bottom: auto;
  }

  :global(html:has(.dashboard-layout) .toast-enter-from) {
    transform: translateY(-20px);
  }

  /* Под полосой звонка (ActiveCallWidget) — её высота: конец страницы над ней. */
  .dashboard-main {
    padding-bottom: calc(1rem + var(--call-strip-height, 0px));
  }
}

/* DEV-2233: на телефоне в крошках остаётся текущий уровень — на полный путь
   в одной строке с бургером и иконками места нет. */
@media (max-width: 600px) {
  .breadcrumb-item:not(:last-child) {
    display: none;
  }

  .header-actions {
    gap: 6px;
  }

  .header-actions__group {
    gap: 2px;
  }
}
</style>
